import Vue from 'vue'
import { PropertyRecord } from '@/types/search/PropertyRecord'
import { AicAicSyncMode, LandingPageEnum } from '@/types/api'
import AicApi from '@/misc/apis/AicApi'
import ObjectApi from '@/misc/apis/ObjectApi'

const AIC_LANDING_PAGE_FASTLANE = 'AIC_LANDING_PAGE_FASTLANE' as LandingPageEnum
const aic = {
  data() {
    return {
      aicEnabled: false,
      constructionInsuranceLoading: false
    }
  },
  computed: {
    accountId() {
      return this.$store.getters['user/getAccountId']
    }
  },
  methods: {
    async checkAicEnabled() {
      if (!this.accountId) {
        this.aicEnabled = false
        return
      }
      try {
        const response = await AicApi.hasAicEnabled(this.accountId)
        this.aicEnabled = response.enabled
      } catch (e) {
        console.error('Failed to check AIC status:', e)
        this.aicEnabled = false
      }
    },
    getAicSyncStatus(property: PropertyRecord) {
      if (!property?.aicSyncMode || !this.accountId) {
        return 'disabled'
      }

      const syncMode = property.aicSyncMode[this.accountId.toString()]
      if (syncMode === AicAicSyncMode.AIC_SYNC_MODE_ENABLED_WITH_SUPPORT_CONFIRMED) {
        return 'confirmed'
      }
      if (
        syncMode === AicAicSyncMode.AIC_SYNC_MODE_ENABLED_WITHOUT_SUPPORT ||
        syncMode === AicAicSyncMode.AIC_SYNC_MODE_ENABLED_WITH_SUPPORT_NOT_CONFIRMED
      ) {
        return 'pending'
      }

      return 'disabled'
    },
    hasAicReportDamageDisabledProperties(properties: PropertyRecord[]): boolean {
      if (!properties || properties.length === 0) {
        return false
      }
      return properties.some(property => this.getAicSyncStatus(property) === 'disabled')
    },
    hasAicWithoutSupportProperties(properties: PropertyRecord[]): boolean {
      if (!properties || properties.length === 0) {
        return false
      }
      return properties.some(property =>
        property?.aicSyncMode && this.accountId &&
        property.aicSyncMode[this.accountId.toString()] === AicAicSyncMode.AIC_SYNC_MODE_ENABLED_WITHOUT_SUPPORT
      )
    },
    hasAicPendingProperties(properties: PropertyRecord[]): boolean {
      if (!properties || properties.length === 0) {
        return false
      }
      return properties.some(property => this.getAicSyncStatus(property) === 'pending')
    },
    isPropertySynchronizedWithSupport(properties: PropertyRecord[]): boolean {
      if (!properties || properties.length === 0) {
        return false
      }
      const property = properties[0]
      if (!property?.aicSyncMode || !this.accountId) {
        return false
      }
      const syncMode = property.aicSyncMode[this.accountId.toString()]
      return syncMode === AicAicSyncMode.AIC_SYNC_MODE_ENABLED_WITH_SUPPORT_NOT_CONFIRMED ||
        syncMode === AicAicSyncMode.AIC_SYNC_MODE_ENABLED_WITH_SUPPORT_CONFIRMED
    },
    isPropertyAicSynced(properties: PropertyRecord[]): boolean {
      if (!properties || properties.length === 0) {
        return false
      }
      return this.getAicSyncStatus(properties[0]) !== 'disabled'
    },
    isAicInsuranceActionsDisabled(properties?: PropertyRecord[]): boolean {
      return !this.aicEnabled || !this.isPropertySynchronizedWithSupport(properties)
    },
    async loadPropertiesForAicCheck(buildingIds: string[]): Promise<{ objectID: string; aicSyncMode: Record<string, string> | null }[]> {
      if (!buildingIds || buildingIds.length === 0) {
        return []
      }
      try {
        const { properties = [] } = await ObjectApi.getPropertiesByBuildingIds(buildingIds)
        if (properties.length === 0) {
          return []
        }
        return await Promise.all(
          properties.map(async (property: { id: string }) => {
            try {
              const aicResponse = await AicApi.getSyncModeForProperty(property.id)
              return { objectID: property.id, aicSyncMode: aicResponse.syncModes || null }
            } catch {
              return { objectID: property.id, aicSyncMode: null }
            }
          })
        )
      } catch (e) {
        console.error('Error loading properties for AIC check:', e)
        return []
      }
    },
    openConstructionInsuranceInAic(property: PropertyRecord) {
      const propertyId = property?.id || property?.objectID
      if (!propertyId || !this.isPropertyAicSynced([property])) {
        return
      }
      this.constructionInsuranceLoading = true
      AicApi.getSsoUrl(AIC_LANDING_PAGE_FASTLANE, String(propertyId))
        .then(response => {
          if (!AicApi.openSsoUrl(response)) {
            Vue.toasted.show(this.$t('message.loadingErrors.aicSsoUrl'), { type: 'error' })
          }
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.aicSsoUrl'), { type: 'error' })
        })
        .finally(() => {
          this.constructionInsuranceLoading = false
        })
    }
  }
}

export { aic }
